import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { BRANDING } from "@/config/branding";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ui, GoldButton } from "@/components/Primitives";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password, remember);
        navigate(u.onboarding_complete ? "/" : "/onboarding");
      } else {
        const u = await register(form);
        if (u?.email_failed) {
          toast.warning(
            "Your account has been created successfully, but we couldn't send your verification email at this time. Please try again shortly or contact support if the problem continues.",
            { duration: 9000 }
          );
        }
        navigate("/onboarding");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative items-center justify-center overflow-hidden bg-[#FBF7F1]">
        <img src="https://customer-assets.emergentagent.com/job_finplan-hub-36/artifacts/w4g0mbwc_ball%20rings.PNG"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(251,247,241,0.55), rgba(246,241,234,0.35))" }} />
        <div className="relative z-10 px-16">
          <div className="font-display text-6xl tracking-[0.08em] gold-text">{BRANDING.name}</div>
          <div className="text-xs uppercase tracking-[0.35em] text-[#A99F90] mt-3 mb-8">{BRANDING.tagline}</div>
          <p className="font-display text-3xl font-light leading-snug text-[#3a352f] max-w-md">
            One calm, secure home for every part of your financial life.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden font-display text-4xl gold-text tracking-wider text-center mb-2">{BRANDING.name}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-light mb-2 text-[#2C2A28]">
            {mode === "login" ? "Welcome home." : "Create your home."}
          </h1>
          <p className="text-[#8A7F70] text-sm mb-8">
            {mode === "login" ? "Sign in to your Financial Home." : "Begin organising your financial life."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className={ui.label}>Full name</Label>
                <Input data-testid="auth-name" value={form.name} onChange={change("name")} required className={ui.input} placeholder="Ada Lovelace" />
              </div>
            )}
            <div>
              <Label className={ui.label}>Email</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={change("email")} required className={ui.input} placeholder="you@email.com" />
            </div>
            <div>
              <Label className={ui.label}>Password</Label>
              <Input data-testid="auth-password" type="password" value={form.password} onChange={change("password")} required className={ui.input} placeholder="••••••••" />
            </div>

            {mode === "login" && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-[#6b6256] cursor-pointer">
                  <Checkbox data-testid="auth-remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-[#D9CDBA] data-[state=checked]:bg-[#C6A45C] data-[state=checked]:border-[#C6A45C]" />
                  Remember me
                </label>
                <Link to="/forgot-password" data-testid="auth-forgot" className="text-sm gold-text hover:underline">Forgot password?</Link>
              </div>
            )}

            {err && <div data-testid="auth-error" className="text-sm text-[#b06b6b] bg-[#C99FA0]/15 border border-[#C99FA0]/30 rounded-xl px-4 py-3">{err}</div>}

            <GoldButton data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-12">
              {loading ? <Loader2 className="animate-spin" size={20} /> : mode === "login" ? "Sign In" : "Create Account"}
            </GoldButton>
          </form>

          <div className="mt-6 text-center text-sm text-[#8A7F70]">
            {mode === "login" ? `New to ${BRANDING.name}?` : "Already have a home?"}{" "}
            <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="gold-text hover:underline font-medium">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
